// Состояние режима «Повторить движение» в GTM (13.10): ролик с отрезком, люди по слотам, нейросеть на выбор, запуск.
//
// Ролик во всех нейросетях живёт в одном месте — штатный слот видео uploadedVideo (handleVideoUpload: формат, длина, вес,
// загрузка на S3; пикер «Загрузить / Из моих генераций»). Отрезок (начало, длина) живёт здесь. Куда ролик и фото уходят
// в запрос, решает вид нейросети (motionTool.motionKind):
//  - replace (P-Video, Wan) и motion (Kling MC): ролик из слота; длина отрезка = selectedVideoDuration (цена и duration
//    запроса, как ползунок обрезки бара); хвост режет сервер, начало не в нуле — отрезок вырезаем до запуска;
//  - edit (Seedance 2.5): ролик зеркалится в видео-референс referenceVideos с длиной отрезка (по ней цена и длина
//    переделки), режим «Референсы», намерение «Переделать», промпт под капотом; Seedance хвост не режет — отрезок короче
//    ролика вырезаем до запуска.
// Люди — слотами (владелец 13.10: «кнопка количества людей в видео, и столько слотов для аватаров»): слот держит аватара
// (главный вид через /assets/plan, как слот героя тренда) или своё фото; порядок слотов = порядок людей слева направо.
// Фото слотов уходят в запрос списком settings.motionPhotos (каналы виджета у замены человека на несколько фото не
// рассчитаны), в каналы кладём их только для показа и штатных проверок. Смена нейросети: слоты остаются, лишние сверх
// её лимита прячутся с конца и возвращаются у нейросети без лимита (владелец 14.10: «Как на фото» сбрасывал аватаров).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGenerate } from '../../../context/GenerateContext'
import { useProviders } from '../../../hooks/useProviders'
import { useAuth } from '../../../context/AuthContext'
import { isStaff } from '../../../utils/roles'
import { useAttachedStyleCard } from '../../../api/attachedStyleCard'
import { motionPeople, trimVideoOnServer, type MotionPerson } from '../../../api/images'
import { repeatLength, type RefVideo } from '../../../api/scene'
import { assetThumb, parseAssetError, planAssets } from '../../../api/assets'
import { getStickyScene, setStickyScene } from '../../../api/sceneSelection'
import { showToast } from '../../../components/Toast'
import { UNFIT_TEXTS } from '../../../utils/characterFit'
import type { Model } from '../../../data/models'
import type { UploadedFile, UploadedVideo } from '../utils/uploadHelpers'
import {
	buildMotionPrompt,
	clipFromUpload,
	clipLimits,
	clipNeedsCut,
	clipSegmentKey,
	maxWho,
	autoTarget,
	motionKind,
	motionModelsFor,
	repinMotionModel,
	targetChoosable,
	whereOf,
	wholeClipLength,
	type MotionPair,
	type ReplaceTarget,
} from './motionTool'

export interface MotionWho {
	id: string
	name: string
	url: string
	avatar: string | null
}
/** Слот человека: аватар из библиотеки, своё фото или пусто. */
export type MotionSlot = { kind: 'avatar'; who: MotionWho } | { kind: 'own'; file: UploadedFile } | null

const videoFile = (name: string) => new File([], name, { type: 'video/mp4' })

// Слоты переживают F5 (владелец 14.10): запись в sessionStorage — живёт столько же, сколько пин режима (вкладка), новая
// вкладка начинает с чистого. Своё фото — ссылкой на S3 (превью-blob после перезагрузки мёртв), аватар — как есть.
const SLOTS_KEY = 'umnik.motion.slots'
type SavedSlot = { kind: 'avatar'; who: MotionWho } | { kind: 'own'; url: string; note?: string } | null
/** Запись вкладки: слоты, число людей и люди из ролика (people — к ролику clip, чтобы после F5 не искать заново). */
interface SavedRecord {
	count: number
	slots: MotionSlot[]
	clip: string | null
	people: MotionPerson[]
	peopleOff: boolean
}
function readSavedSlots(): SavedRecord | null {
	try {
		const raw = sessionStorage.getItem(SLOTS_KEY)
		if (!raw) return null
		const p = JSON.parse(raw) as { count?: number; slots?: SavedSlot[]; clip?: string | null; people?: MotionPerson[]; peopleOff?: boolean }
		if (!Array.isArray(p.slots) || !p.slots.length) return null
		const slots: MotionSlot[] = p.slots.map((s) =>
			!s ? null
			: s.kind === 'avatar' ? { kind: 'avatar', who: s.who }
			: { kind: 'own', file: { file: new File([], 'photo'), preview: s.url, url: s.url, note: s.note } },
		)
		const count = Math.max(1, Math.min(slots.length, Math.floor(Number(p.count) || slots.length)))
		const people = Array.isArray(p.people) ? p.people.filter((x) => x && typeof x.desc === 'string') : []
		// Ролик запоминаем только вместе с людьми: пустой разбор (сбой, ролик без людей) после F5 пробуем заново.
		return { count, slots, clip: people.length && typeof p.clip === 'string' ? p.clip : null, people, peopleOff: !!p.peopleOff }
	} catch {
		return null
	}
}
function writeSavedSlots(rec: SavedRecord): void {
	try {
		if (rec.count === 1 && !rec.slots.some(Boolean) && !rec.people.length) {
			sessionStorage.removeItem(SLOTS_KEY)
			return
		}
		const slots: SavedSlot[] = rec.slots.map((s) =>
			!s ? null
			: s.kind === 'avatar' ? { kind: 'avatar', who: s.who }
			: s.file.url ? { kind: 'own', url: s.file.url, note: s.file.note } : null,
		)
		sessionStorage.setItem(SLOTS_KEY, JSON.stringify({ ...rec, slots }))
	} catch {
		// хранилище недоступно — без восстановления
	}
}
export type PeopleStatus = 'idle' | 'loading' | 'done' | 'failed'
const pathOf = (u?: string | null) => (u || '').split('?', 1)[0]
const slotUrl = (s: MotionSlot): string => (s ? (s.kind === 'avatar' ? s.who.url : s.file.url || '') : '')
// Фото аватара уже в S3 — файл-заглушка, как у слота героя тренда; sceneAsset — зеркало слотов такие фото не считает своими.
const slotFile = (s: NonNullable<MotionSlot>): UploadedFile =>
	s.kind === 'avatar' ? { file: new File([], 'avatar'), preview: s.who.url, url: s.who.url, sceneAsset: true } : s.file
const sameFiles = (a: UploadedFile[], b: UploadedFile[]) => a.length === b.length && a.every((x, i) => pathOf(x.url) === pathOf(b[i].url))
const isMotionPrompt = (s: string) => s.startsWith('Use @Video1 as the base')

export function useMotionMode({ enabled, cardId, isGuest }: { enabled: boolean; cardId: string | null; isGuest: boolean }) {
	const gen = useGenerate()
	// Тот же список, что у штатного пикера виджета (widgetModels — видимость в виджете решает админ; стафф — весь,
	// юзер — нейросеть пина, см. motionModelsFor).
	const { widgetModels } = useProviders()
	const auth = useAuth()
	const staff = isStaff(auth.user?.role)
	const pinnedModelId = useAttachedStyleCard()?.targetModelId ?? null
	const models = useMemo(() => motionModelsFor(widgetModels, { staff, pinnedModelId }), [widgetModels, staff, pinnedModelId])
	const model = gen.selectedModel
	const kind = motionKind(model)
	const where = whereOf(kind)
	const limits = clipLimits(model, gen.settings.characterOrientation)

	// ── Ролик ──
	const [clip, setClip] = useState<RefVideo | null>(null)
	// Вырезанный отрезок, подставленный вместо исходника: исходник остаётся в clip — ручки двигаются по нему.
	const cutRef = useRef<{ key: string; url: string } | null>(null)
	const upload = gen.uploadedVideo
	// Пропорция ролика из плеера зоны (MotionZone.onMeta): по ней рамка результата — Seedance и замена отдают ролик в
	// формате входа (владелец 14.10: «слот результата подстроить под формат ролика, а не под параметры нейросети»).
	const [clipAspect, setClipAspect] = useState<number | null>(null)
	const capRef = useRef(limits.cap)
	capRef.current = limits.cap
	const onClipMeta = useCallback((m: { seconds: number; aspect: number | null }) => {
		setClipAspect(m.aspect)
		// Длину ролика берём и отсюда: в readonly (идёт генерация, гость) зона не шлёт onChange, и после F5 во время
		// генерации отрезок оставался 10 с по умолчанию при ролике в 16 с (14.10) — цена и вырезка были бы не те.
		if (!(m.seconds > 0)) return
		setClip((cur) => {
			if (!cur) return cur
			const known = Number(cur.seconds)
			if (known > 0 && Math.abs(known - m.seconds) <= 0.25) return cur
			const next: RefVideo = { ...cur, seconds: Math.round(m.seconds * 100) / 100 }
			if (!cur.length) next.length = wholeClipLength(m.seconds, capRef.current)
			return next
		})
	}, [])
	useEffect(() => {
		if (!enabled) return
		if (!upload) {
			cutRef.current = null
			setClip(null)
			setClipAspect(null)
			return
		}
		// Ролик ещё грузится (превью без ссылки) — отрезок прежний; в слоте наш отрезок — исходник уже в clip.
		if (!upload.url || cutRef.current?.url === upload.url) return
		setClip((cur) => (cur?.url === upload.url ? cur : clipFromUpload(upload, limits.cap)))
	}, [enabled, upload, limits.cap])
	// Ролик вернулся из снимка загрузок (F5, переход между страницами) без своей длины: цена на кнопке считает секунды
	// ролика, только когда его длина известна (utils/modelCost, videoDrivenDuration), иначе берёт 5 с — а запрос уходит
	// с длиной отрезка, и сервер отказывает «Цена изменилась» (19.10, P-Video: 375 у сервера против 125 на кнопке).
	// Длину знает плеер зоны — пишем её в ролик слота, как обычная загрузка (useGenerateUploads), и цена считается
	// штатно, как у всех нейросетей (владелец 19.10: «цену показываем в кнопке, даже если она пересчиталась»).
	useEffect(() => {
		if (!enabled || !upload?.url || Number(upload.duration) > 0) return
		const seconds = Number(clip?.seconds)
		if (!(seconds > 0) || clip?.url !== upload.url) return
		gen.setUploadedVideo({ ...upload, duration: seconds })
	}, [enabled, upload, clip?.url, clip?.seconds]) // eslint-disable-line react-hooks/exhaustive-deps
	const length = clip ? repeatLength(clip, limits.cap, limits.min) : 0
	// Длина отрезка = секунды к оплате и в запросе у замены и Kling (как ползунок обрезки бара).
	useEffect(() => {
		if (!enabled || !clip || !length) return
		if (gen.selectedVideoDuration !== length) gen.setSelectedVideoDuration(length)
	}, [enabled, clip, length]) // eslint-disable-line react-hooks/exhaustive-deps
	// Seedance: ролик зеркалится в видео-референс с длиной отрезка — по ней цена на кнопке, сводка и длина переделки.
	// Уже вырезанный отрезок (тот же ключ) остаётся на месте.
	useEffect(() => {
		if (!enabled || kind !== 'edit') return
		const url = clip?.url ? (cutRef.current?.key === clipSegmentKey(clip, length) ? cutRef.current.url : clip.url) : null
		gen.setReferenceVideos((prev) => {
			if (!url) return prev.length ? [] : prev
			const cur = prev[0]
			if (prev.length === 1 && cur?.url === url && cur.duration === length && cur.billedSeconds === length) return prev
			// billedSeconds: на запуск уйдёт отрезок ровно этой длины (вырезка ниже) — вход платы за секунды видео он, а не
			// исходник целиком (иначе кнопка считала бы весь ролик и гасла по балансу зря).
			return [{ file: videoFile(clip?.name || 'clip.mp4'), preview: url, url, duration: length, billedSeconds: length }]
		})
	}, [enabled, kind, clip, length]) // eslint-disable-line react-hooks/exhaustive-deps
	const onClipChange = useCallback(
		(patch: Partial<RefVideo>) => {
			setClip((cur) => {
				if (!cur) return cur
				const next = { ...cur, ...patch }
				// Длину узнал плеер — отрезок на весь ролик, пока человек не выбрал свой.
				if (patch.seconds && !cur.length) next.length = wholeClipLength(patch.seconds, limits.cap)
				return next
			})
		},
		[limits.cap],
	)
	const removeClip = useCallback(() => {
		cutRef.current = null
		setClip(null)
		gen.setUploadedVideo(null)
		gen.setReferenceVideos((prev) => (prev.length ? [] : prev))
		// Люди принадлежат ролику: ролика нет — нет и их (слоты с аватарами остаются, человек их ещё пристроит).
		peopleClip.current = null
		setPeople([])
		setPeopleStatus('idle')
		setPeopleOff(false)
	}, [gen])
	const dropClip = useCallback(
		(file: File) => {
			void gen.handleVideoUpload({ target: { files: [file] } })
		},
		[gen],
	)

	// ── Кто повторит: слоты по числу людей ──
	// Число людей — выбор человека, слоты живут при нём (all). Лимит нейросети (maxWho) только прячет лишние с конца
	// (shown): у нейросети без лимита они снова на месте — владелец 14.10: «Как на фото» (Kling, один человек) сбрасывал
	// выбранных аватаров. В запрос и каналы идут только видимые слоты.
	const max = maxWho(model)
	// Сеть берёт человека сама (Wan, Kling): по людям активен только первый — самый заметный (motionTool.autoTarget).
	const auto = autoTarget(model)
	// Начальное состояние — из записи вкладки (F5): ленивые инициализаторы отрабатывают до эффектов, и липкий аватар
	// ниже не затирает восстановленный первый слот.
	const saved = useRef(readSavedSlots())
	const [count, setCountState] = useState(() => saved.current?.count ?? 1)
	const [all, setAll] = useState<MotionSlot[]>(() => saved.current?.slots ?? [null])

	// ── Кто в ролике: люди словами и плитками (services/motion_people, 14.10) ──
	// Сервер смотрит четыре кадра ролика и описывает людей; аватар кладут на конкретного человека, промпт описывает его
	// (buildMotionPrompt.people). Разбор идёт после загрузки ролика (ответ сервер держит сутки — F5 и смена нейросети
	// не ждут), сбой или ролик без людей — слоты по порядку, как раньше. Человек может вернуть порядок сам (peopleOff):
	// распознавание ошибается — пропускает, двоит, режет не того.
	const [people, setPeople] = useState<MotionPerson[]>(() => saved.current?.people ?? [])
	const [peopleStatus, setPeopleStatus] = useState<PeopleStatus>(() => (saved.current?.people.length ? 'done' : 'idle'))
	const [peopleOff, setPeopleOff] = useState(() => saved.current?.peopleOff ?? false)
	const peopleClip = useRef<string | null>(saved.current?.clip ?? null)
	useEffect(() => {
		if (!enabled) return
		const url = clip?.url
		// Ролика нет — людей не трогаем: при F5 ролик возвращается из снимка загрузок эффектом позже, и запись вкладки с
		// людьми не должна пропасть за этот миг; убрал ролик сам человек — чистит removeClip. Другой ролик — ищем заново.
		if (!url) return
		const path = pathOf(url)
		if (peopleClip.current === path) return
		peopleClip.current = path
		setPeopleOff(false)
		setPeopleStatus('loading')
		const ctrl = new AbortController()
		motionPeople({ video_url: url }, ctrl.signal)
			.then((r) => {
				if (ctrl.signal.aborted) return
				setPeople(r.measured ? r.people : [])
				setPeopleStatus(r.measured ? 'done' : 'failed')
			})
			.catch(() => {
				if (ctrl.signal.aborted) return
				setPeople([])
				setPeopleStatus('failed')
			})
		return () => ctrl.abort()
	}, [enabled, clip?.url])
	// Режим «по людям»: люди найдены и человек его не выключил. Люди из ролика показываются при любой нейросети
	// (владелец 18.10: «найти мы должны всех, а если сеть меняет одного, то аватара кладут на одного, остальные плитки
	// мьютятся с объяснением»): лимит сети ограничивает назначения (assign ниже), а не список людей. У Wan и Kling наш
	// выбор «кого» не уходит — аватар только у первого (самого заметного) человека, остальные слоты скрыты (slots ниже).
	const peopleMode = people.length > 0 && !peopleOff
	useEffect(() => {
		if (!enabled) return
		writeSavedSlots({ count, slots: all, clip: peopleClip.current, people, peopleOff })
	}, [enabled, count, all, people, peopleOff])
	// Видимые слоты: по людям — слот на каждого (аватаров не больше лимита нейросети), иначе — сколько выбрал человек,
	// но не больше лимита.
	const shown = peopleMode ? people.length : Math.min(count, max)
	useEffect(() => {
		if (!peopleMode) return
		setAll((s) => (s.length >= people.length ? s : [...s, ...Array.from({ length: people.length - s.length }, () => null)]))
	}, [peopleMode, people.length])
	// Сеть берёт человека сама: виден и уходит в запрос только слот первого (самого заметного); аватары в других слотах
	// (положены при Seedance) не пропадают — вернутся при смене нейросети (владелец 14.10: Kling сбрасывал выбранных).
	const slots = useMemo(() => {
		const v = all.slice(0, shown)
		return auto && peopleMode ? v.map((s, i) => (i === 0 ? s : null)) : v
	}, [all, shown, auto, peopleMode])
	const shownRef = useRef(shown)
	shownRef.current = shown
	const assigned = slots.filter(Boolean).length
	const peopleModeRef = useRef(peopleMode)
	peopleModeRef.current = peopleMode
	const maxRef = useRef(max)
	maxRef.current = max
	const autoRef = useRef(auto)
	autoRef.current = auto
	const [target, setTarget] = useState<ReplaceTarget>('largest')
	// Слот, чьё меню открыли: туда и встанет выбранный аватар или своё фото.
	const pendingSlot = useRef<number | null>(null)
	useEffect(() => {
		if (target === 'all' && kind !== 'edit') setTarget('largest')
	}, [target, kind])
	const setCount = useCallback(
		(n: number) => {
			const c = Math.max(1, Math.min(max, Math.floor(n)))
			if (c === shownRef.current) return
			setCountState(c)
			setAll((s) => {
				const next = s.slice(0, c)
				while (next.length < c) next.push(null)
				return next
			})
		},
		[max],
	)
	const openSlot = useCallback((i: number) => {
		pendingSlot.current = i
	}, [])
	const assign = useCallback((slot: NonNullable<MotionSlot>, index?: number) => {
		let grow = false
		setAll((s) => {
			const next = [...s]
			// Сеть берёт самого заметного сама (Wan, Kling): аватар только к первому, занятый — заменяется.
			if (peopleModeRef.current && autoRef.current) {
				next[0] = slot
				return next
			}
			let n = shownRef.current
			let wanted = index ?? pendingSlot.current ?? next.findIndex((x, i) => i < n && !x)
			// По порядку: все слоты заняты, а человек выбрал ещё аватара — это «ещё один человек», а не замена последнего
			// (владелец 14.10 выбрал четырёх подряд в один слот, и ушёл только последний). Слотов не больше лимита.
			if (wanted < 0 && !peopleModeRef.current && n < maxRef.current) {
				grow = true
				wanted = n
				n += 1
				while (next.length < n) next.push(null)
			}
			const at = wanted >= 0 && wanted < n ? wanted : n - 1
			// По людям: аватаров не больше, чем нейросеть заменит; занятый слот можно поменять.
			if (peopleModeRef.current && !next[at] && next.slice(0, n).filter(Boolean).length >= maxRef.current) return s
			next[at] = slot
			return next
		})
		if (grow) setCountState((c) => Math.min(maxRef.current, c + 1))
		pendingSlot.current = null
	}, [])
	// Файл ещё грузится (превью без ссылки) или пикер поставил заглушку — каналы не трогаем.
	const uploading = (!!gen.uploadedImage && !gen.uploadedImage.url) || gen.referenceImages.some((f) => !f.url)
	// Своё фото пришло штатным путём (загрузка, «Из моих генераций»): встаёт в открытый слот, иначе в первый пустой.
	// Снимок localStorage (restored — GenerateContext возвращает ссылки прошлого захода при входе и смене нейросети) —
	// не своё фото: 14.10 такая ссылка вставала третьим «Ваше фото»; зеркало ниже такие каналы перепишет слотами.
	useEffect(() => {
		if (!enabled || uploading) return
		const known = new Set(all.map((s) => pathOf(slotUrl(s))).filter(Boolean))
		const fresh = [gen.uploadedImage, ...gen.referenceImages].find(
			(f): f is UploadedFile => !!f?.url && !f.sceneAsset && !f.restored && !known.has(pathOf(f.url)),
		)
		if (fresh) assign({ kind: 'own', file: fresh })
	}, [enabled, uploading, gen.uploadedImage, gen.referenceImages]) // eslint-disable-line react-hooks/exhaustive-deps
	// Каналы виджета — зеркало слотов (для показа и штатных проверок): Seedance читает фото из референсов, замена и
	// Kling — из слота фото; в запрос фото идут списком settings.motionPhotos ниже.
	useEffect(() => {
		if (!enabled || uploading) return
		const files = slots.filter((s): s is NonNullable<MotionSlot> => !!s).map(slotFile)
		if (kind === 'edit') {
			if (!sameFiles(gen.referenceImages, files)) gen.setReferenceImages(() => files)
			if (gen.uploadedImage) gen.setUploadedImage(null)
		} else {
			const first = files[0] ?? null
			if (pathOf(gen.uploadedImage?.url) !== pathOf(first?.url)) gen.setUploadedImage(first)
			const rest = files.slice(1)
			if (!sameFiles(gen.referenceImages, rest)) gen.setReferenceImages(() => rest)
		}
	}, [enabled, kind, slots, uploading]) // eslint-disable-line react-hooks/exhaustive-deps
	const applyWho = useCallback(
		async (id: string, quiet = false, index?: number) => {
			if (!model) return
			try {
				const plan = await planAssets({ asset_ids: [id], adapter: model.adapter || model.provider, model: model.id, task: 'video' })
				const item = plan.layout[0]
				const hero = plan.assets.find((a) => a.id === item?.asset_id && a.kind === 'character')
				if (!plan.fits || !hero || !item?.url) {
					if (!quiet) {
						const asked = plan.assets.find((a) => a.id === id)
						const reason = asked && asked.status !== 'ready' ? 'NOT_BUILT' : plan.reason ?? ''
						showToast(UNFIT_TEXTS[reason] ?? 'Аватар не подходит этой нейросети', 'info')
					}
					return
				}
				assign({ kind: 'avatar', who: { id: hero.id, name: hero.name, url: item.url, avatar: assetThumb(hero) } }, index)
				// В «липкий» выбор сцены НЕ пишем (14.10): он общий с классическим виджетом, и четыре аватара режима
				// раскладывались там двенадцатью видами, которые сброс возвращал. Слоты режима живут в записи вкладки.
			} catch (e) {
				if (!quiet) showToast(`Не удалось применить аватар: ${parseAssetError(e)}`, 'error')
			}
		},
		[model, assign],
	)
	// Снял аватара сам — до смены карточки слот сам не заполняется (иначе липкий выбор тут же вернулся бы).
	// Липкий выбор только читаем (герой тренда встаёт первым слотом) и снимаем из него убранного аватара.
	const dismissedFor = useRef<string | null>(null)
	const anyFilled = all.some(Boolean)
	useEffect(() => {
		if (!enabled || isGuest || anyFilled || !model) return
		if (dismissedFor.current === (cardId ?? '')) return
		const sticky = getStickyScene()
		if (sticky?.ids.length) void applyWho(sticky.ids[0], true, 0)
	}, [enabled, isGuest, anyFilled, model?.id, cardId]) // eslint-disable-line react-hooks/exhaustive-deps
	const clearSlot = useCallback(
		(i: number) => {
			const s = slots[i]
			if (s?.kind === 'avatar') {
				dismissedFor.current = cardId ?? ''
				const cur = getStickyScene()
				if (cur) setStickyScene(cur.ids.filter((x) => x !== s.who.id), cur.prefer, cur.paths)
			}
			setAll((prev) => prev.map((x, j) => (j === i ? null : x)))
		},
		[slots, cardId],
	)
	/** «Очистить» (владелец 18.10): ролик с людьми, аватары со всех плиток, число людей — к одному, результат — в ноль.
	 *  Режим, нейросеть и «Где будет» остаются (в отличие от × шапки, который закрывает инструмент). */
	const clearAll = useCallback(() => {
		removeClip()
		const avatarIds = all.flatMap((s) => (s?.kind === 'avatar' ? [s.who.id] : []))
		if (avatarIds.length) {
			dismissedFor.current = cardId ?? ''
			const cur = getStickyScene()
			if (cur) setStickyScene(cur.ids.filter((x) => !avatarIds.includes(x)), cur.prefer, cur.paths)
		}
		setAll((prev) => prev.map(() => null))
		setCountState(1)
		gen.resetResult()
	}, [removeClip, all, cardId, gen])

	// ── Промпт под капотом и фото по слотам в запрос ──
	// Порядок фото в запросе — по людям: как люди появляются в ролике (first_t, при равенстве — по заметности, как нашёл
	// сервер). Разбор прогонов 13–14.10: Seedance 2.5 ставит референсы по порядку появления людей (@Image1 — первому
	// появившемуся), а текст «кто кого» не читает; фото в этом порядке — единственное, что он соблюдает.
	const order = useMemo(() => {
		const idx = slots.map((_, i) => i)
		if (!peopleMode) return idx
		return idx.sort((a, b) => (Number(people[a]?.first_t ?? 0) - Number(people[b]?.first_t ?? 0)) || a - b)
	}, [peopleMode, slots, people])
	const photos = useMemo(() => order.map((i) => slots[i]).map(slotUrl).filter(Boolean), [order, slots])
	const photosKey = photos.join('|')
	// По людям: хватает одного аватара (остальные остаются как есть); по порядку — все слоты заняты.
	const filled = peopleMode ? assigned > 0 : slots.length > 0 && slots.every(Boolean)
	// Пары «кто → фото» в том же порядке, что photos (оба — по order).
	const pairs = useMemo<MotionPair[] | undefined>(
		() => (peopleMode
			? order
				.map((i): MotionPair | null => (slots[i] && people[i] ? { desc: people[i].desc, position: people[i].position ?? null, firstT: people[i].first_t ?? null } : null))
				.filter((x): x is MotionPair => x !== null)
			: undefined),
		[peopleMode, order, slots, people],
	)
	const prompt = buildMotionPrompt({ kind, count: shown, target, people: pairs })
	useEffect(() => {
		if (!enabled) return
		const want = photos.length ? photos : undefined
		if ((gen.settings.motionPhotos ?? []).join('|') !== (want ?? []).join('|')) gen.updateSetting('motionPhotos', want)
		// Подсказка «кто кого» — только P-Video читает её (instruction_prompt); остальным сервер не шлёт.
		const instr = kind === 'replace' && prompt ? prompt : undefined
		if (gen.settings.motionInstruction !== instr) gen.updateSetting('motionInstruction', instr)
	}, [enabled, kind, photosKey, prompt, gen.settings.motionPhotos, gen.settings.motionInstruction]) // eslint-disable-line react-hooks/exhaustive-deps
	// Seedance: режим «Референсы», намерение «Переделать», промпт переделки; сброс настроек при смене модели это не
	// отменяет — эффект ставит заново. Другим нейросетям наш промпт не нужен.
	useEffect(() => {
		if (!enabled) return
		if (kind === 'edit' && gen.selectedModel && motionKind(gen.selectedModel) === 'edit') {
			if (gen.settings.seedanceMode !== 'references') gen.updateSetting('seedanceMode', 'references')
			if (gen.settings.videoIntent !== 'edit') gen.updateSetting('videoIntent', 'edit')
			// Звук — как выбрал человек в пилюле (владелец 14.10): Seedance оставляет дорожку исходника только при
			// включённом «Звуке», у трендов дорожку кладёт админка.
			if (gen.prompt !== prompt) gen.setPrompt(prompt)
		} else if (isMotionPrompt(gen.prompt)) gen.setPrompt('')
	}, [enabled, kind, gen.selectedModel?.id, gen.settings.seedanceMode, gen.settings.videoIntent, gen.prompt, prompt]) // eslint-disable-line react-hooks/exhaustive-deps
	// Режим закрыли — служебные поля запроса не должны утечь в обычный виджет.
	const updateSettingRef = useRef(gen.updateSetting)
	updateSettingRef.current = gen.updateSetting
	useEffect(() => {
		if (!enabled) return
		return () => {
			updateSettingRef.current('motionPhotos', undefined)
			updateSettingRef.current('motionInstruction', undefined)
		}
	}, [enabled])
	// Звук всегда включён (владелец 19.10): у Seedance своя дорожка (sound), у замены и Kling — звук ролика
	// (keepOriginalSound); переключателей в режиме нет (SettingsPillsRow, MobileSettingsSheet). Сброс настроек при смене
	// нейросети включаем обратно. Выбор человека вне режима не трогаем: на выходе возвращаем, что было.
	const soundBefore = useRef<{ sound: boolean; keep: boolean } | null>(null)
	const settingsRef = useRef(gen.settings)
	settingsRef.current = gen.settings
	useEffect(() => {
		if (!enabled) return
		soundBefore.current ??= { sound: settingsRef.current.sound, keep: settingsRef.current.keepOriginalSound }
		return () => {
			const b = soundBefore.current
			soundBefore.current = null
			if (!b) return
			updateSettingRef.current('sound', b.sound)
			updateSettingRef.current('keepOriginalSound', b.keep)
		}
	}, [enabled])
	useEffect(() => {
		if (!enabled) return
		if (!gen.settings.sound) gen.updateSetting('sound', true)
		if (!gen.settings.keepOriginalSound) gen.updateSetting('keepOriginalSound', true)
	}, [enabled, gen.settings.sound, gen.settings.keepOriginalSound]) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Нейросеть на выбор ──
	// Выбор — только в штатном пикере виджета (WidgetModelPicker: в режиме список его нейросетей двумя группами «Как в
	// ролике» / «Как на фото», пин следует за выбором). Слоты остаются, каналы перестраивает зеркало выше.
	useEffect(() => {
		if (!enabled || !model || !kind) return
		// Видео-референс нужен только переделке Seedance; у остальных ролик идёт из слота.
		if (kind !== 'edit' && gen.referenceVideos.length) gen.setReferenceVideos(() => [])
	}, [enabled, model?.id, kind]) // eslint-disable-line react-hooks/exhaustive-deps
	const switchModel = useCallback(
		(m: Model) => {
			if (!motionKind(m) || m.id === model?.id) return
			repinMotionModel(m)
			gen.setSelectedModel(m)
		},
		[gen, model?.id],
	)
	// ── Запуск ──
	const [cutting, setCutting] = useState(false)
	// Ждём, пока отрезок (или возвращённый исходник) сядет в канал, — handleGenerate читает каналы из состояния.
	const [pending, setPending] = useState<string | null>(null)
	const missing = !clip?.url || !filled
	const launch = useCallback(async () => {
		if (!clip?.url || !filled || !length || !kind) return
		const needsCut = clipNeedsCut(clip, length, kind)
		let url = clip.url
		if (needsCut) {
			const key = clipSegmentKey(clip, length)
			const cached = cutRef.current?.key === key ? cutRef.current.url : null
			if (cached) url = cached
			else {
				setCutting(true)
				try {
					const start = Number(clip.start) || 0
					const r = await trimVideoOnServer(clip.url, start, start + length)
					url = r.url
					cutRef.current = { key, url }
				} catch {
					gen.setError('Не удалось вырезать отрезок ролика — попробуйте ещё раз')
					return
				} finally {
					setCutting(false)
				}
			}
		} else cutRef.current = null
		if (kind === 'edit') {
			// Seedance читает видео-референс: зеркало с нужным отрезком (эффект выше держит его в такт).
			const cur = gen.referenceVideos[0]
			if (cur?.url === url && cur.duration === length) {
				gen.handleGenerate()
				return
			}
			gen.setReferenceVideos(() => [{ file: videoFile('segment.mp4'), preview: url, url, duration: length, billedSeconds: length }])
			setPending(url)
			return
		}
		if (upload?.url === url) {
			gen.handleGenerate()
			return
		}
		// Отрезок встаёт в слот вместо исходника (или исходник возвращается после прошлого отрезка).
		const item: UploadedVideo = { file: videoFile(needsCut ? 'segment.mp4' : clip.name || 'clip.mp4'), preview: url, url, duration: needsCut ? length : clip.seconds ?? length }
		gen.setUploadedVideo(item)
		setPending(url)
	}, [clip, filled, length, kind, upload?.url, gen])
	useEffect(() => {
		if (!pending) return
		const landed = kind === 'edit' ? gen.referenceVideos[0]?.url === pending && gen.referenceVideos[0]?.duration === length : upload?.url === pending && gen.selectedVideoDuration === length
		if (!landed) return
		setPending(null)
		gen.handleGenerate()
	}, [pending, kind, upload?.url, gen.referenceVideos, gen.selectedVideoDuration, length]) // eslint-disable-line react-hooks/exhaustive-deps

	return {
		model,
		models,
		kind,
		where,
		limits,
		clip,
		length,
		/** Ролик грузится: превью уже есть, ссылки ещё нет. */
		busy: !!upload && !upload.url,
		/** Пропорция ролика (ширина / высота) из плеера; null — ролика нет или плеер ещё не прочитал. */
		clipAspect,
		onClipMeta,
		/** Людей в ролике — сколько выбрал человек, но не больше лимита нейросети; столько же слотов. */
		count: shown,
		max,
		slots,
		filled,
		target,
		/** Кого заменить можно выбрать: аватар один и нейросеть понимает указание (Seedance, P-Video). */
		targetChoosable: !peopleMode && shown === 1 && targetChoosable(model),
		/** Люди из ролика (сервер): слот на каждого, аватаров не больше max. */
		people,
		peopleMode,
		peopleStatus,
		peopleOff,
		setPeopleOff,
		/** Сколько аватаров положено (по людям). */
		assigned,
		/** Сеть берёт человека сама (Wan, Kling): активна только плитка самого заметного. */
		autoTarget: auto,
		/** Аватар есть хоть в одном слоте, включая скрытые лимитом нейросети. */
		anyFilled,
		cutting,
		missing,
		onClipChange,
		removeClip,
		dropClip,
		setCount,
		setTarget,
		openSlot,
		clearSlot,
		clearAll,
		applyWho,
		switchModel,
		launch,
	}
}

export type MotionMode = ReturnType<typeof useMotionMode>
